import React, { useCallback, useState } from 'react';
import LikeButton from '../components/atoms/LikeButton';
import CommentToggle from '../components/atoms/CommentToggle';
import PostVisibilityControl from '../components/molecules/PostVisibilityControl';
import {
    BAND_POST_VISIBILITY_OPTIONS,
    SHAREABLE_OBJECT_VISIBILITY_OPTIONS,
} from '../components/molecules/PostVisibilityControl/options';
import { postsService } from '../services/api/posts';
import { eventService } from '../services/eventService';
import { normalizeScheduledEntityFormVisibility } from '../constants/scheduledEntityVisibility';
import { toBandPostUiVisibility, toUiVisibility } from '../utils/visibility';
import { isPostTimelineItem, isScheduledEntityTimelineItem } from '../utils/timelineItems';
import { invalidateDiscoverMarkersCache } from './useDiscoverMarkers';

export const LIKE_EMOJI = '❤️';

export const isReactedByViewer = (entry, viewerId) => {
    if (!entry) return false;
    if (typeof entry.reacted === 'boolean') return entry.reacted;
    if (!viewerId || !Array.isArray(entry.users)) return false;
    return entry.users.some((userId) => String(userId) === String(viewerId));
};

const canManageTimelineItem = (item) => item?.canManage === true;

const isBandPostTimelineItem = (item) => (
    isPostTimelineItem(item)
    && String(item?.relatedObject?.objectType || '').trim().toUpperCase() === 'BAND'
);

const resolvePostVisibilityForTimeline = (item) => (
    isBandPostTimelineItem(item)
        ? toBandPostUiVisibility(item?.visibility || item?.visibilityLabel)
        : toUiVisibility(item?.visibility || item?.visibilityLabel)
);

function buildOptimisticLikeReactions(reactions, liked, existing, viewerId) {
    const viewerKey = viewerId ? String(viewerId) : null;
    const usersWithoutViewer = (entry) => (
        Array.isArray(entry?.users) && viewerKey
            ? entry.users.filter((userId) => String(userId) !== viewerKey)
            : (Array.isArray(entry?.users) ? entry.users.slice() : [])
    );

    if (liked) {
        const nextCount = Math.max(0, (Number(existing?.count) || 0) - 1);
        return nextCount === 0
            ? reactions.filter((entry) => entry?.emoji !== LIKE_EMOJI)
            : reactions.map((entry) => (
                entry?.emoji === LIKE_EMOJI
                    ? { ...entry, count: nextCount, reacted: false, users: usersWithoutViewer(entry) }
                    : entry
            ));
    }

    if (existing) {
        return reactions.map((entry) => (
            entry?.emoji === LIKE_EMOJI
                ? {
                    ...entry,
                    count: (Number(entry.count) || 0) + 1,
                    reacted: true,
                    users: viewerKey
                        ? [...usersWithoutViewer(entry), viewerKey]
                        : (Array.isArray(entry.users) ? entry.users : []),
                }
                : entry
        ));
    }

    return [
        ...reactions,
        { emoji: LIKE_EMOJI, count: 1, reacted: true, users: viewerKey ? [viewerKey] : [] },
    ];
}

export default function useTimelineEngagement({
    currentUserId,
    onItemMetaChange,
    readOnly = false,
}) {
    const [busyVisibilityIds, setBusyVisibilityIds] = useState({});
    const [openPostId, setOpenPostId] = useState(null);
    const [openScheduledEntityCommentsId, setOpenScheduledEntityCommentsId] = useState(null);

    const handleLikeToggle = useCallback(async (item) => {
        if (readOnly) return;
        if (!item?.id) return;
        const itemIsPost = isPostTimelineItem(item);
        const itemIsScheduledEntity = isScheduledEntityTimelineItem(item);
        const canReact = itemIsPost ? item?.canReact !== false : item?.canReact === true;
        if (!canReact || (!itemIsPost && !itemIsScheduledEntity)) return;

        const previousReactions = Array.isArray(item.reactions) ? item.reactions : [];
        const existing = previousReactions.find((entry) => entry?.emoji === LIKE_EMOJI) || null;
        const liked = isReactedByViewer(existing, currentUserId);
        const optimisticReactions = buildOptimisticLikeReactions(
            previousReactions,
            liked,
            existing,
            currentUserId,
        );

        onItemMetaChange?.(item.id, { reactions: optimisticReactions });

        try {
            const updatedReactions = itemIsPost
                ? (
                    liked
                        ? await postsService.removeReaction(item.id, LIKE_EMOJI)
                        : await postsService.addReaction(item.id, LIKE_EMOJI)
                )
                : (
                    liked
                        ? await eventService.removeScheduledEntityReaction(item.objectType, item.id, LIKE_EMOJI)
                        : await eventService.addScheduledEntityReaction(item.objectType, item.id, LIKE_EMOJI)
                );
            const normalized = Array.isArray(updatedReactions)
                ? updatedReactions.map((entry) => (
                    typeof entry?.reacted === 'boolean'
                        ? entry
                        : { ...entry, reacted: isReactedByViewer(entry, currentUserId) }
                ))
                : updatedReactions;
            onItemMetaChange?.(item.id, { reactions: normalized });
        } catch (error) {
            console.error('Failed to toggle timeline item like', error);
            onItemMetaChange?.(item.id, { reactions: previousReactions });
        }
    }, [currentUserId, onItemMetaChange, readOnly]);

    const handleVisibilityChange = useCallback(async (item, nextVisibility) => {
        if (readOnly) return;
        if (!item?.id) return;
        const itemIsPost = isPostTimelineItem(item);
        const itemIsScheduledEntity = isScheduledEntityTimelineItem(item);
        if (!itemIsPost && !itemIsScheduledEntity) return;

        const currentVisibility = itemIsPost
            ? resolvePostVisibilityForTimeline(item)
            : normalizeScheduledEntityFormVisibility(item.visibility);
        const normalizedNext = itemIsPost
            ? (isBandPostTimelineItem(item) ? toBandPostUiVisibility(nextVisibility) : toUiVisibility(nextVisibility))
            : normalizeScheduledEntityFormVisibility(nextVisibility);
        if (normalizedNext === currentVisibility) return;

        setBusyVisibilityIds((prev) => ({ ...prev, [item.id]: true }));
        try {
            const updated = itemIsPost
                ? await postsService.updatePost(item.id, { ...item, visibility: normalizedNext })
                : await eventService.updateScheduledEntityVisibility(item.objectType, item, normalizedNext);

            if (itemIsScheduledEntity) {
                invalidateDiscoverMarkersCache();
            }

            onItemMetaChange?.(item.id, {
                visibility: updated.visibility,
                visibilityLabel: updated.visibilityLabel,
                updatedAt: updated.updatedAt,
            });
        } catch (error) {
            console.error('Failed to update timeline item visibility', error);
        } finally {
            setBusyVisibilityIds((prev) => {
                const next = { ...prev };
                delete next[item.id];
                return next;
            });
        }
    }, [onItemMetaChange, readOnly]);

    const renderSocialFooter = useCallback((item, {
        commentTriggerClassName,
        visibilityWrapperClassName,
    } = {}) => {
        const itemIsPost = isPostTimelineItem(item);
        const itemIsScheduledEntity = isScheduledEntityTimelineItem(item);
        if (!itemIsPost && !itemIsScheduledEntity) {
            return null;
        }

        const likeEntry = Array.isArray(item?.reactions)
            ? item.reactions.find((entry) => entry?.emoji === LIKE_EMOJI)
            : null;
        const liked = isReactedByViewer(likeEntry, currentUserId);
        const likeCount = Number(likeEntry?.count) || 0;
        const comments = Number(item?.commentsCount) || 0;
        const isReadOnly = readOnly === true;
        const canReact = !isReadOnly && (itemIsPost ? item?.canReact !== false : item?.canReact === true);
        const canComment = !isReadOnly && (itemIsPost ? item?.canComment !== false : item?.canComment === true);
        const canManage = !isReadOnly && canManageTimelineItem(item);
        const itemIsBandPost = isBandPostTimelineItem(item);
        const visibilityValue = itemIsPost
            ? resolvePostVisibilityForTimeline(item)
            : normalizeScheduledEntityFormVisibility(item?.visibility);
        const visibilityOptions = itemIsPost
            ? (itemIsBandPost ? BAND_POST_VISIBILITY_OPTIONS : undefined)
            : SHAREABLE_OBJECT_VISIBILITY_OPTIONS;

        return (
            <>
                <LikeButton
                    liked={liked}
                    count={likeCount}
                    disabled={!canReact}
                    onToggle={() => handleLikeToggle(item)}
                />
                <span
                    className={commentTriggerClassName}
                    onClick={(event) => { event.stopPropagation(); event.preventDefault(); }}
                    onMouseDown={(event) => event.stopPropagation()}
                >
                    <CommentToggle
                        count={comments}
                        disabled={!canComment}
                        onToggle={() => {
                            if (!canComment) return;
                            if (itemIsPost) {
                                setOpenPostId(item.id);
                            } else {
                                setOpenScheduledEntityCommentsId(item.id);
                            }
                        }}
                    />
                </span>
                {canManage ? (
                    <div className={visibilityWrapperClassName}>
                        <PostVisibilityControl
                            value={visibilityValue}
                            onChange={(next) => handleVisibilityChange(item, next)}
                            disabled={Boolean(busyVisibilityIds[item.id])}
                            size="sm"
                            className={`post-card__visibility-control${busyVisibilityIds[item.id] ? ' is-busy' : ''}`}
                            options={visibilityOptions}
                        />
                    </div>
                ) : null}
            </>
        );
    }, [busyVisibilityIds, currentUserId, handleLikeToggle, handleVisibilityChange, readOnly]);

    return {
        busyVisibilityIds,
        openPostId,
        setOpenPostId,
        openScheduledEntityCommentsId,
        setOpenScheduledEntityCommentsId,
        renderSocialFooter,
    };
}
